/**
 * Normalizes websocket `ai_response` payloads and HTTP `result` objects for AI chat.
 */

function parseMaybeJson(payload) {
  if (payload == null) return null;
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }
  return payload;
}

/**
 * @param {unknown} payload - Raw socket payload (object or JSON string)
 * @returns {object|null} Top-level event object with `event` and `data`
 */
export function normalizeStreamEvent(payload) {
  const p = parseMaybeJson(payload);
  if (!p || typeof p !== 'object') return null;

  // ccchat backend wrapper: { chunk: { event, data, id? }, conversationId }
  if (p.chunk && typeof p.chunk === 'object' && p.chunk.event) {
    const inner = p.chunk;
    const mergedData = (inner.data && typeof inner.data === 'object')
      ? { ...inner.data, conversation_id: inner.data.conversation_id || p.conversationId }
      : { conversation_id: p.conversationId };
    return { event: inner.event, data: mergedData, id: inner.id };
  }

  if (p.event && p.data) return p;
  if (p.data && p.data.event && p.data.data) {
    return { event: p.data.event, data: p.data.data, id: p.data.id };
  }
  return null;
}

export function extractConversationIdFromStreamEvent(eventObj) {
  if (!eventObj || !eventObj.data) return null;
  const { data } = eventObj;
  if (data.conversation_id) return data.conversation_id;
  if (data.data && data.data.metadata && data.data.metadata.conversation_id) {
    return data.data.metadata.conversation_id;
  }
  return null;
}

export function extractPartialAnswerFromStreamEvent(eventObj) {
  if (!eventObj || eventObj.event !== 'llm_token_stream') return null;
  const inner = eventObj.data && eventObj.data.data;
  if (!inner || !inner.partial_answer) return null;
  return inner.partial_answer;
}

export function extractCompleteAnswerFromStreamEvent(eventObj) {
  if (!eventObj) return null;
  if (eventObj.event === 'llm_response_complete') {
    const r = eventObj.data && eventObj.data.data && eventObj.data.data.response;
    if (typeof r === 'string') return r;
  }
  if (eventObj.event === 'final_answer') {
    const d = eventObj.data && eventObj.data.data;
    if (!d) return null;
    const a = d.final_answer || d.answer;
    if (typeof a === 'string') return a;
  }
  if (eventObj.event === 'workflow_complete') {
    const fa = eventObj.data && eventObj.data.data && eventObj.data.data.final_answer;
    if (typeof fa === 'string') return fa;
  }
  return null;
}

export function conversationIdFromHttpResult(result) {
  if (!result || typeof result !== 'object') return null;
  if (result.conversationId) return result.conversationId;
  if (result.conversation_id) return result.conversation_id;
  return null;
}

/**
 * Prefer explicit completion events; otherwise last llm_token_stream partial_answer.
 * @param {Array<{ event: string, data: object }>} responseArr
 * @returns {string}
 */
export function fullAnswerFromResponseArray(responseArr) {
  if (!Array.isArray(responseArr) || responseArr.length === 0) return '';
  let lastPartial = '';
  let complete = null;
  responseArr.forEach((item) => {
    if (!item || !item.data) return;
    const { event } = item;
    const d = item.data;
    if (event === 'llm_token_stream' && d.data && typeof d.data.partial_answer === 'string') {
      lastPartial = d.data.partial_answer;
    }
    if (event === 'llm_response_complete' && d.data && typeof d.data.response === 'string') {
      complete = d.data.response;
    }
    if (event === 'final_answer' && d.data) {
      const fa = d.data.final_answer || d.data.answer;
      if (typeof fa === 'string') complete = fa;
    }
    if (event === 'workflow_complete' && d.data && typeof d.data.final_answer === 'string') {
      complete = d.data.final_answer;
    }
  });
  if (complete != null && complete !== '') return complete;
  return lastPartial;
}
